"use client";

import { useState, useMemo } from "react";
import { useTranslations } from "next-intl";
import { Check, ChevronLeft, Search } from "lucide-react";
import { compareCurrencyCodes, REFERENCE_FX_CURRENCY_CODES } from "@/config/currencies";
import { getCurrencyNarrowSymbol } from "@/lib/currency-display";
import { cn } from "@/lib/utils";

const POPULAR: ReadonlyArray<string> = ["EUR", "USD", "GBP", "CHF"];

function getCurrencyName(code: string, locale: string): string {
	try {
		return new Intl.DisplayNames(locale, { type: "currency" }).of(code) ?? code;
	} catch {
		return code;
	}
}

export interface CurrencyDetailPanelProps {
	currentLocale: string;
	currentCurrency: string;
	onBack: () => void;
	onSelect: (code: string) => void;
}

export function CurrencyDetailPanel({
	currentLocale,
	currentCurrency,
	onBack,
	onSelect,
}: CurrencyDetailPanelProps) {
	const t = useTranslations("localeChannel");
	const [query, setQuery] = useState("");

	const { popular, rest } = useMemo(() => {
		const q = query.trim().toLowerCase();
		const codes = [...REFERENCE_FX_CURRENCY_CODES] as string[];
		const matches = (code: string) => {
			if (!q) return true;
			const name = getCurrencyName(code, currentLocale).toLowerCase();
			return code.toLowerCase().includes(q) || name.includes(q);
		};
		const popularFiltered = POPULAR.filter((code) => codes.includes(code) && matches(code));
		const restSorted = (codes.filter((code) => !POPULAR.includes(code)) as string[])
			.sort(compareCurrencyCodes)
			.filter(matches);
		return { popular: popularFiltered, rest: restSorted };
	}, [query, currentLocale]);

	const renderRow = (code: string, tone: "popular" | "all") => {
		const isActive = code === currentCurrency;
		const sym = getCurrencyNarrowSymbol(code, currentLocale);
		const name = getCurrencyName(code, currentLocale);
		return (
			<button
				key={code}
				type="button"
				aria-current={isActive ? "true" : undefined}
				onClick={() => onSelect(code)}
				className={cn(
					"flex min-h-[44px] w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
					isActive
						? "bg-primary text-primary-foreground"
						: tone === "popular"
							? "bg-muted/40 hover:bg-muted/60 text-foreground"
							: "hover:bg-muted/50 text-foreground",
				)}
			>
				{sym !== code ? (
					<span className="w-7 text-center text-base font-semibold tabular-nums" aria-hidden="true">
						{sym}
					</span>
				) : (
					<span className="w-7 shrink-0" aria-hidden="true" />
				)}
				<div className="min-w-0 flex-1">
					<div className="text-sm font-semibold">{code}</div>
					<div
						className={cn(
							"truncate text-xs",
							isActive ? "text-primary-foreground/80" : "text-muted-foreground",
						)}
					>
						{name}
					</div>
				</div>
				{isActive ? <Check className="h-4 w-4 shrink-0" aria-hidden /> : null}
			</button>
		);
	};

	return (
		<div className="flex h-[36rem] min-h-0 flex-auto flex-col">
			<div className="flex shrink-0 items-center gap-2 border-b border-border bg-muted-soft py-3 pl-4 pr-16">
				<button
					type="button"
					onClick={onBack}
					aria-label={t("back")}
					className="-ml-1 inline-flex h-11 w-11 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
				>
					<ChevronLeft className="h-4 w-4" />
				</button>
				<h3 className="text-sm font-semibold text-foreground">{t("currencyLabel")}</h3>
			</div>

			<div className="shrink-0 px-4 py-3">
				<div className="flex min-h-11 items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 focus-within:border-ring focus-within:ring-2 focus-within:ring-ring">
					<Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
					<input
						type="search"
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						placeholder={t("searchCurrencyPlaceholder")}
						className="min-w-0 flex-1 border-0 bg-transparent p-0 text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-0 md:text-sm"
						aria-label={t("searchCurrencyPlaceholder")}
					/>
				</div>
			</div>

			<div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 pb-4 [scrollbar-color:var(--muted-foreground)_transparent] [scrollbar-gutter:stable] [scrollbar-width:thin]">
				{popular.length > 0 ? (
					<section className="pt-1">
						<h4 className="px-2 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
							{t("popularCurrencies")}
						</h4>
						<div className="flex flex-col gap-1">{popular.map((code) => renderRow(code, "popular"))}</div>
					</section>
				) : null}

				{rest.length > 0 ? (
					<section className="pt-4">
						<h4 className="px-2 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
							{t("allCurrencies")}
						</h4>
						<div className="flex flex-col gap-0.5">{rest.map((code) => renderRow(code, "all"))}</div>
					</section>
				) : null}

				{popular.length === 0 && rest.length === 0 ? (
					<p className="px-2 py-6 text-center text-xs text-muted-foreground">{t("noResults")}</p>
				) : null}
			</div>
		</div>
	);
}
